/**
 * History-API router for the library app.
 *
 * The server serves apps/library/index.html for every /library/* path, then
 * this router reads location.pathname, matches against a route table, and
 * swaps the right screen into <main id="lib-root">.
 *
 * Why history-API over hash routing:
 *   - /library/g/snes/zelda-a-link-to-the-past is clean and indexable.
 *   - Express already has the SPA fallback wired up (mirrors /u, /admin).
 *   - Matches the existing main app's route conventions.
 *
 * Routes are declared in main.js via `register(pattern, handler)`. Pattern
 * syntax: `:param` captures a segment; no wildcards beyond that in Phase 1.
 */

import { state } from './state.js';
import { setPageTitle } from '../../../src/shared/page-title.js';
import { trackPageView } from '../../../src/client/page-views.js';

const routes = []; // [{ pattern, keys, regex, handler, title }]

function compilePattern(pattern) {
  const keys = [];
  const regexSrc = pattern.replace(/:(\w+)/g, (_, key) => {
    keys.push(key);
    return '([^/]+)';
  });
  return { regex: new RegExp(`^${regexSrc}/?$`), keys };
}

/**
 * Register a route. `handler` receives `(params, { root, isCurrent })` — root
 * is the <main> element it should render into. Async handlers must check
 * `isCurrent()` after awaited work before mutating root. Return `false` when a
 * handled load failure/not-found state was rendered; any other resolved value
 * means content rendered successfully and is eligible for analytics.
 */
export function register(pattern, handler, {
  title = 'Game Library', analyticsPage = null, analyticsSubId = null,
} = {}) {
  const { regex, keys } = compilePattern(pattern);
  routes.push({ pattern, keys, regex, handler, title, analyticsPage, analyticsSubId });
}

function match(pathname) {
  for (const route of routes) {
    const m = pathname.match(route.regex);
    if (m) {
      const params = {};
      route.keys.forEach((k, i) => { params[k] = decodeURIComponent(m[i + 1]); });
      return { route, params };
    }
  }
  return null;
}

/**
 * Programmatic navigation. Updates history, triggers popstate handler.
 * Use instead of <a href> when you want to avoid a full page load.
 *
 * Before navigating, we save the current scroll position into
 * history.state so the browser back button can restore it. This is
 * the backbone of the "click game → back → same scroll position" UX.
 */
export function navigate(path, { replace = false, scroll = true } = {}) {
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  if (scroll) window.scrollTo(0, 0);
  return render({ focus: true, announce: true });
}

function resolveTitle(route, params) {
  return typeof route.title === 'function' ? route.title(params) : route.title;
}

function updateRouteStatus(title) {
  const status = document.getElementById('lib-route-status');
  if (!status) return;
  status.textContent = '';
  queueMicrotask(() => {
    status.textContent = `Loaded ${title}`;
  });
}

let _renderGeneration = 0;

async function render({ focus = false, announce = false } = {}) {
  const generation = ++_renderGeneration;
  const isCurrent = () => generation === _renderGeneration;
  const root = document.getElementById('lib-root');
  if (!root) return false;

  const found = match(location.pathname);
  if (!found) {
    setPageTitle('Page not found');
    renderNotFound(root);
    state.update('route', { path: location.pathname, params: {} });
    if (announce) updateRouteStatus('Page not found');
    if (focus) root.focus({ preventScroll: true });
    return false;
  }

  state.update('route', { path: location.pathname, params: found.params });
  const title = resolveTitle(found.route, found.params);
  setPageTitle(title);

  let succeeded = false;
  try {
    let outcome = found.route.handler(found.params, { root, isCurrent });
    if (outcome && typeof outcome.then === 'function') outcome = await outcome;
    if (!isCurrent()) return false;

    // A matched URL is not necessarily a rendered page. Emit only after the
    // handler resolves with content so failures do not look like reach.
    succeeded = outcome !== false;
    if (succeeded && found.route.analyticsPage) {
      const subId = typeof found.route.analyticsSubId === 'function'
        ? found.route.analyticsSubId(found.params) : found.route.analyticsSubId;
      trackPageView(found.route.analyticsPage, { subId });
    }
  } catch (err) {
    if (!isCurrent()) return false;
    console.error('[library/router] handler threw:', err);
    setPageTitle('Library error');
    renderError(root, err);
  }

  if (!isCurrent()) return false;
  updateNavHighlight();
  if (announce) updateRouteStatus(succeeded ? title : 'Page unavailable');
  if (focus) root.focus({ preventScroll: true });
  return succeeded;
}

function renderNotFound(root) {
  root.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'lib-empty-state';
  wrap.innerHTML = `
    <h1>Not found</h1>
    <p>The page <code></code> doesn't exist in the library.</p>
    <p><a class="lib-btn" href="/library">Back to library</a></p>
  `;
  wrap.querySelector('code').textContent = location.pathname;
  root.appendChild(wrap);
}

function renderError(root, err) {
  root.replaceChildren();
  const wrap = document.createElement('div');
  wrap.className = 'lib-empty-state';
  wrap.innerHTML = `
    <h1>Something broke</h1>
    <p>An error happened rendering this screen.</p>
    <pre class="lib-error-detail"></pre>
    <p><a class="lib-btn" href="/library">Back to library</a></p>
  `;
  wrap.querySelector('.lib-error-detail').textContent = String(err?.stack || err);
  root.appendChild(wrap);
}

/**
 * Toggle `.active` on nav links whose `data-route` matches the current path
 * (exact or prefix for nested routes).
 */
function updateNavHighlight() {
  const current = location.pathname.replace(/\/$/, '') || '/';
  const links = document.querySelectorAll('.lib-nav-link');

  let bestTarget = null;
  let bestLen = 0;
  for (const link of links) {
    const target = link.getAttribute('data-route');
    if (!target) continue;
    const isMatch = target === '/library'
      ? current === '/library'
      : current === target || current.startsWith(target + '/');
    if (isMatch && target.length > bestLen) {
      bestTarget = target;
      bestLen = target.length;
    }
  }

  for (const link of links) {
    const active = link.getAttribute('data-route') === bestTarget;
    link.classList.toggle('active', active);
    if (active) link.setAttribute('aria-current', 'page');
    else link.removeAttribute('aria-current');
  }
}

// Stable references so the listeners can be removed on teardown (used by
// tests; production code calls start() once and never unmounts).
let _clickHandler = null;
let _popstateHandler = null;

/**
 * Intercept clicks on same-origin <a> tags so in-app navigation feels instant
 * (no server round-trip just to render the same index.html again).
 */
function attachLinkInterceptor() {
  // Guard against double-attach if start() is called twice.
  if (_clickHandler) return;

  _clickHandler = (e) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return; // left-click only
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // new-tab intent
    const a = e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;
    // Only intercept relative links under /library. Everything else (main
    // app routes like /, /u, external URLs, #anchors) goes through normally.
    if (!href.startsWith('/library')) return;
    // target=_blank / download opts out
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download')) return;

    e.preventDefault();
    navigate(href);
  };
  _popstateHandler = () => { void render({ focus: true, announce: true }); };

  document.addEventListener('click', _clickHandler);
  window.addEventListener('popstate', _popstateHandler);
}

/**
 * Kick off the router. Call after routes are registered.
 */
export function start() {
  attachLinkInterceptor();
  return render();
}

/**
 * Teardown for tests. Removes the click + popstate listeners and clears
 * the routes table so the next test can start from a clean slate without
 * listener pile-up across vi.resetModules() cycles.
 *
 * Production code never needs this — start() is called once and the app
 * runs until the page unloads.
 */
export function _resetForTests() {
  // Invalidate any handler still awaiting I/O so it cannot report a view or
  // overwrite test/next-page chrome when it eventually settles.
  _renderGeneration++;
  if (_clickHandler) {
    document.removeEventListener('click', _clickHandler);
    _clickHandler = null;
  }
  if (_popstateHandler) {
    window.removeEventListener('popstate', _popstateHandler);
    _popstateHandler = null;
  }
  routes.length = 0;
}
